import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Building,
  LayoutDashboard,
  Building2,
  Layers,
  DoorOpen,
  Users,
  ClipboardList,
  BedDouble,
  Receipt,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Settings,
  Calendar,
  LifeBuoy
} from 'lucide-react';

const AdminLayout = ({ children }) => {
  const { admin, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isSuperAdmin = admin?.role === 'super_admin';

  const superAdminLinks = [
    { to: '/superadmin', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/superadmin/stations', icon: Building2, label: 'Stations' },
    { to: '/superadmin/categories', icon: Layers, label: 'Categories' },
    { to: '/superadmin/rooms', icon: DoorOpen, label: 'Rooms' },
    { to: '/superadmin/admins', icon: Users, label: 'Admins' },
    { to: '/superadmin/support', icon: LifeBuoy, label: 'Support Desk' },
    { to: '/superadmin/settings', icon: Settings, label: 'Settings' },
  ];

  const stationAdminLinks = [
    { to: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/admin/requests', icon: ClipboardList, label: 'Booking Requests' },
    { to: '/admin/schedule', icon: Calendar, label: 'Booking Schedule' },
    { to: '/admin/rooms', icon: BedDouble, label: 'Room Status' },
    { to: '/admin/billing', icon: Receipt, label: 'Billing History' },
    { to: '/admin/support', icon: LifeBuoy, label: 'Support Tickets' },
  ];

  const navLinks = isSuperAdmin ? superAdminLinks : stationAdminLinks;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-midnight-50">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-72 bg-gradient-to-b from-midnight-950 via-midnight-900 to-midnight-950 
        transform transition-transform duration-300 ease-in-out lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <Link to="/" className="flex items-center gap-3">
              <img src="/Retiring Room Icon.svg" alt="Retiring Room" className="w-14 h-14" />
              <div>
                <h1 className="text-lg font-display font-bold text-white">Retiring Room</h1>
                <p className="text-xs text-railway-400">
                  {isSuperAdmin ? 'Super Admin' : 'Station Admin'}
                </p>
              </div>
            </Link>
            <button
              className="lg:hidden text-white/60 hover:text-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-6 px-4 space-y-1 overflow-y-auto">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.to;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200
                    ${isActive
                      ? 'bg-railway-500 text-white shadow-lg shadow-railway-500/30'
                      : 'text-white/60 hover:bg-white/5 hover:text-white'
                    }
                  `}
                >
                  <link.icon className="w-5 h-5" />
                  <span className="font-medium">{link.label}</span>
                  {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                </Link>
              );
            })}

            {/* Super admin can also access station admin panel */}
            {isSuperAdmin && (
              <>
                <div className="pt-4 pb-2 px-4">
                  <p className="text-xs font-semibold text-white/40 uppercase tracking-wider">
                    Station Operations
                  </p>
                </div>
                {stationAdminLinks.map((link) => {
                  const isActive = location.pathname === link.to;
                  return (
                    <Link
                      key={link.to}
                      to={link.to}
                      className={`
                        flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200
                        ${isActive
                          ? 'bg-midnight-700 text-white'
                          : 'text-white/60 hover:bg-white/5 hover:text-white'
                        }
                      `}
                    >
                      <link.icon className="w-5 h-5" />
                      <span className="font-medium">{link.label}</span>
                    </Link>
                  );
                })}
              </>
            )}
          </nav>

          {/* User Info & Logout */}
          <div className="p-4 border-t border-white/10">
            <div className="mb-4 px-4">
              <p className="text-white font-medium truncate">{admin?.name}</p>
              <p className="text-white/40 text-sm truncate">{admin?.email}</p>
              {admin?.assignedStation && (
                <p className="text-railway-400 text-xs mt-1">
                  {admin.assignedStation.name}
                </p>
              )}
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 w-full px-4 py-3 text-red-400 hover:bg-red-500/10 rounded-xl transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-72">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-lg border-b border-midnight-100">
          <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8 h-16">
            <button
              className="lg:hidden text-midnight-600 hover:text-midnight-900"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-4 ml-auto">
              <span className="text-sm text-midnight-500">
                {new Date().toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
